import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import moment from 'moment'
import {
  InsiBatchResultResponseDTO,
  InsiResultDTO,
  CheckInsiDTO,
} from '@services/InsiService'

interface InsiBatchResultDetailsProps {
  result: InsiBatchResultResponseDTO
  submittedList: (CheckInsiDTO & { patientId: number })[]
}

const formatIns = (value: string, cle?: string | null) =>
  cle ? `${value}${cle}` : value

const formatDateTime = (value: string) => {
  if (!value) return 'Non communiqué'
  const parsed = moment(value)
  if (!parsed.isValid()) return 'Non communiqué'
  return parsed.format('DD/MM/YYYY HH:mm')
}

const getPatientLabel = (
  index: number,
  submittedList: (CheckInsiDTO & { patientId: number })[],
) => {
  const submitted = submittedList[index]
  if (!submitted) {
    return ''
  }
  return `${submitted.beneficiairePrenom} ${submitted.beneficiaireNom}`.trim()
}

export const InsiBatchResultDetails: React.FC<InsiBatchResultDetailsProps> = ({
  result,
  submittedList,
}) => {
  const entries: InsiResultDTO[] = result.message?.resultats ?? []

  if (!entries.length) {
    return (
      <Box
        sx={{
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'divider',
          padding: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Aucun résultat détaillé n’a été retourné pour ce lot.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        padding: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        backgroundColor: 'background.default',
      }}
    >
      {entries.map((entry, index) => {
        const patientLabel = getPatientLabel(index, submittedList)
        const statusColor = entry.erreur ? 'error' : 'success'
        return (
          <Box
            key={`${entry.insValue}-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {patientLabel || `Entrée ${index + 1}`}
              </Typography>
              <Chip
                size="small"
                color={statusColor}
                label={entry.erreur ? 'Échec' : 'Succès'}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              INS : {formatIns(entry.insValue, entry.insCle)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Message : {entry.erreurMessage || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Retour le : {formatDateTime(entry.dateHeureRetour)}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export default InsiBatchResultDetails
